import { useEffect, useRef, useState } from "react";

/**
 * Animates a number counting up from 0 to `target` over `duration`ms, starting only once
 * `active` becomes true (typically driven by an IntersectionObserver, so the count-up plays
 * when the stat scrolls into view rather than immediately on mount). Plain requestAnimationFrame
 * with an ease-out curve -- no dependency on a third-party DOM-manipulating library.
 *
 * Replaces react-odometerjs, which this project used everywhere a stat counter appears
 * (TrustBar.js, SmartTechAcademy.js, Newsletter.js) but whose animation reliably got stuck
 * showing 0 -- confirmed live: its internal width-calculation spacer had the correct target
 * digit, but the visible ribbon never animated to it, in every one of those call sites. Rather
 * than debug a flaky imperative-DOM integration with React's own reconciliation, this hook
 * guarantees the real number always ends up on screen, animated or not.
 */
export function useCountUp(target, active, duration = 1200) {
  const [value, setValue] = useState(0);
  const frameRef = useRef(null);
  const startRef = useRef(null);

  useEffect(() => {
    const numericTarget = Number(target) || 0;

    if (!active) {
      setValue(0);
      startRef.current = null;
      return undefined;
    }

    const tick = (timestamp) => {
      if (startRef.current === null) startRef.current = timestamp;
      const elapsed = timestamp - startRef.current;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      setValue(Math.round(eased * numericTarget));
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(tick);
      }
    };

    frameRef.current = requestAnimationFrame(tick);
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      startRef.current = null;
    };
    // `duration` deliberately omitted: it's a per-call constant, not meant to retrigger the
    // animation if a caller's other state happens to re-render it.
  }, [target, active]); // eslint-disable-line react-hooks/exhaustive-deps

  return value;
}
